import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TopBar } from "@/components/TopBar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  ArrowLeft, ShieldCheck, User, UserPlus, X, Clock, Trash, ArrowsClockwise as RefreshCw, Key, EnvelopeSimple
} from "@phosphor-icons/react";
import { toast } from "sonner";

export default function AdminUsers() {
  const { user: me } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [renotifying, setRenotifying] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, i] = await Promise.all([api.get("/users"), api.get("/invites")]);
      setUsers(u.data);
      setInvites(i.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const reNotifyBidders = async () => {
    const msg =
      "Send an email to every invited bidder on every OPEN auction with the current platform URL?\n\n" +
      "One consolidated email per bidder (listing all their auctions), not one email per auction.\n" +
      "Disqualified bidders are skipped.\n\nContinue?";
    if (!window.confirm(msg)) return;
    setRenotifying(true);
    try {
      const { data } = await api.post("/admin/re-notify-bidders");
      const { bidders_notified, auctions_covered, errors, app_public_url } = data;
      if (errors && errors.length) {
        toast.error(
          `Sent ${bidders_notified} email(s) across ${auctions_covered} auction(s); ${errors.length} failed. Check backend logs.`,
        );
      } else {
        toast.success(
          `Notified ${bidders_notified} bidder(s) across ${auctions_covered} open auction(s). Links point to ${app_public_url || "APP_PUBLIC_URL"}.`,
        );
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to send re-notify emails");
    } finally { setRenotifying(false); }
  };
  const toggleRole = async (u) => {
    const nextRole = u.role === "admin" ? "bidder" : "admin";
    const confirmMsg = nextRole === "admin"
      ? `Promote ${u.name || u.email} to admin? They will be able to create auctions and manage users.`
      : `Demote ${u.name || u.email} to bidder? They will lose admin access.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      await api.post(`/users/${u.id}/role`, { role: nextRole });
      toast.success(`Role updated: ${u.email} is now ${nextRole}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const deleteUser = async (u) => {
    const msg = `Permanently delete ${u.name || u.email} (${u.email})?\n\nThey will lose all access immediately. Their past bids and any auctions they may have won remain in the system for the record.\n\nThis cannot be undone.`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(`Deleted ${u.email}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const resetPassword = async (u) => {
    const msg = `Reset password for ${u.name || u.email}?\n\nA temporary password will be generated and emailed to ${u.email}. They can log in with it and change it from the top bar.\n\nTheir bid history, role, and account stay intact — only the password changes.`;
    if (!window.confirm(msg)) return;
    try {
      const { data } = await api.post(`/users/${u.id}/reset-password`);
      // Show the temp password so the admin can WhatsApp/share it manually if the email is slow.
      window.alert(
        `${data.message}\n\nTemporary password (copy this for backup):\n${data.temp_password}\n\nLogin URL:\n${data.login_url}`,
      );
      toast.success(`Password reset for ${u.email}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to reset password");
    }
  };

  const revokeInvite = async (inv) => {
    if (!window.confirm(`Revoke invite for ${inv.email}? They will no longer be able to use the existing link.`)) return;
    try {
      await api.delete(`/invites/${inv.id}`);
      toast.success("Invite revoked");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const reissueInvite = async ({ email, auction_id = null, confirmMessage } = {}) => {
    const targetEmail = (email || window.prompt("Email of the bidder whose invite is broken / used:") || "").trim();
    if (!targetEmail) return;
    if (confirmMessage && !window.confirm(confirmMessage)) return;
    try {
      const { data } = await api.post("/invites/reissue", { email: targetEmail, auction_id });
      if (data.user_exists) {
        const cta = data.cta_url
          ? `\n\nDirect link they should use:\n${data.cta_url}`
          : "";
        window.alert(`${data.message}${cta}`);
      } else {
        const linkLine = data.cta_url ? `\n\nFresh invite link (also emailed):\n${data.cta_url}` : "";
        window.alert(`${data.message}${linkLine}`);
      }
      toast.success(data.user_exists ? "User already exists — sent login/auction link" : "Fresh invite sent");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to reissue invite");
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="admin-users-page">
      <TopBar />
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-10">
        <button onClick={() => navigate("/")} className="text-xs tracking-[0.25em] uppercase text-neutral-500 flex items-center gap-1.5 mb-4 hover:text-black" data-testid="back-to-dashboard">
          <ArrowLeft size={12} weight="bold" /> Dashboard
        </button>

        <div className="border-b border-black pb-3 mb-8 flex items-baseline justify-between gap-3 flex-wrap">
          <h1 className="font-display font-extrabold text-3xl lg:text-4xl tracking-tightest">User Management</h1>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={reNotifyBidders}
              disabled={renotifying}
              className="rounded-none h-10 px-3 border-[#0D9488] text-[#0D9488] hover:bg-[#0D9488] hover:text-white text-xs tracking-[0.18em] uppercase font-semibold"
              data-testid="re-notify-bidders-cta"
              title="Send a fresh email with the current platform URL to every bidder on every open auction"
            >
              <EnvelopeSimple size={12} weight="bold" className="mr-1.5" />
              {renotifying ? "Sending..." : "Re-notify Bidders"}
            </Button>
            <Button
              variant="outline"
              onClick={() => reissueInvite()}
              className="rounded-none h-10 px-3 border-black/30 text-xs tracking-[0.18em] uppercase font-semibold"
              data-testid="fix-invite-cta"
              title="Reissue or repair a broken invite for a specific email"
            >
              <RefreshCw size={12} weight="bold" className="mr-1.5" /> Fix Invite
            </Button>
            <InviteUserDialog
              open={inviteOpen}
              onOpenChange={setInviteOpen}
              onCreated={load}
            />
          </div>
        </div>

        {/* Pending Invites */}
        <section className="mb-12" data-testid="pending-invites-section">
          <div className="flex items-baseline justify-between mb-2">
            <h2 className="font-display font-extrabold text-xl tracking-tightest">Pending Invites</h2>
            <span className="text-[10px] tracking-[0.25em] uppercase text-neutral-500">{invites.length} pending</span>
          </div>
          {loading ? (
            <div className="py-6 text-xs tracking-[0.3em] uppercase text-neutral-500">Loading...</div>
          ) : invites.length === 0 ? (
            <div className="border border-dashed border-black/15 p-6 text-sm text-neutral-500">
              No pending invites. Click <strong>Invite user</strong> above to send one.
            </div>
          ) : (
            <div className="divide-y divide-black/10 border-y border-black/10">
              {invites.map((inv) => (
                <div key={inv.id} className="grid grid-cols-12 gap-3 py-3 items-center" data-testid={`invite-row-${inv.id}`}>
                  <div className="col-span-12 md:col-span-5">
                    <div className="flex items-center gap-3">
                      <Clock size={14} weight="bold" className="text-neutral-500" />
                      <div className="text-sm font-mono-plex">{inv.email}</div>
                    </div>
                  </div>
                  <div className="col-span-4 md:col-span-2">
                    <Badge
                      variant="outline"
                      className={`rounded-none text-[10px] tracking-[0.25em] uppercase font-semibold ${inv.role === "admin" ? "border-klein-blue text-[#22D3EE]" : "border-black/30"}`}
                    >
                      {inv.role}
                    </Badge>
                  </div>
                  <div className="col-span-4 md:col-span-3 text-xs text-neutral-500">
                    {inv.auction_id ? "Auction invite" : "Direct invite"} · by {inv.invited_by_name || "—"}
                  </div>
                  <div className="col-span-4 md:col-span-2 flex md:justify-end gap-2">
                    <Button
                      variant="outline"
                      onClick={() => reissueInvite({ email: inv.email, auction_id: inv.auction_id, confirmMessage: `Reissue invite for ${inv.email}? This revokes the current pending invite, generates a fresh link, and emails it.` })}
                      className="rounded-none border-black/30 h-8 px-2 text-xs tracking-[0.18em] uppercase font-semibold"
                      data-testid={`reissue-invite-${inv.id}`}
                      title="Send a fresh invite link"
                    >
                      <RefreshCw size={12} weight="bold" className="mr-1" /> Reissue
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => revokeInvite(inv)}
                      className="rounded-none border-black/30 h-8 px-2 text-xs tracking-[0.18em] uppercase font-semibold"
                      data-testid={`revoke-invite-${inv.id}`}
                    >
                      <X size={12} weight="bold" className="mr-1" /> Revoke
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Active Users */}
        <section data-testid="active-users-section">
          <div className="flex items-baseline justify-between mb-2">
            <h2 className="font-display font-extrabold text-xl tracking-tightest">Active Users</h2>
            <span className="text-[10px] tracking-[0.25em] uppercase text-neutral-500">{users.length} users</span>
          </div>
          {loading ? (
            <div className="py-6 text-xs tracking-[0.3em] uppercase text-neutral-500">Loading...</div>
          ) : (
            <div className="divide-y divide-black/10 border-y border-black/10">
              {users.map((u) => (
                <div key={u.id} className="grid grid-cols-12 gap-4 py-4 items-center" data-testid={`user-row-${u.id}`}>
                  <div className="col-span-12 md:col-span-5">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-neutral-100 flex items-center justify-center">
                        {u.role === "admin" ? <ShieldCheck size={16} weight="bold" /> : <User size={16} weight="bold" />}
                      </div>
                      <div>
                        <div className="font-display font-extrabold text-lg tracking-tightest leading-tight">{u.name || "—"}</div>
                        <div className="text-xs font-mono-plex text-neutral-600">{u.email}</div>
                      </div>
                    </div>
                  </div>
                  <div className="col-span-6 md:col-span-3 text-sm text-neutral-600">
                    {u.company || <span className="text-neutral-400">—</span>}
                  </div>
                  <div className="col-span-6 md:col-span-2">
                    <Badge
                      variant={u.role === "admin" ? "default" : "outline"}
                      className={`rounded-none text-[10px] tracking-[0.25em] uppercase font-semibold ${u.role === "admin" ? "bg-[#22D3EE] text-[#0B1929] font-semibold border-klein-blue hover:bg-klein-blue" : "border-black/30"}`}
                      data-testid={`user-role-${u.id}`}
                    >
                      {u.role}
                    </Badge>
                  </div>
                  <div className="col-span-12 md:col-span-2 md:flex md:justify-end md:gap-2">
                    {u.id !== me?.id ? (
                      <div className="flex gap-2 justify-end">
                        <Button
                          variant="outline"
                          onClick={() => resetPassword(u)}
                          className="rounded-none border-black/30 h-9 w-9 p-0"
                          title="Reset password (emails the user a temp password)"
                          data-testid={`reset-password-${u.id}`}
                        >
                          <Key size={14} weight="bold" />
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => toggleRole(u)}
                          className="rounded-none border-black h-9 px-3 text-xs tracking-[0.18em] uppercase font-semibold"
                          data-testid={`toggle-role-${u.id}`}
                        >
                          {u.role === "admin" ? "Demote" : "Promote"}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => deleteUser(u)}
                          className="rounded-none border-red-500 text-red-600 hover:bg-red-50 hover:text-red-700 h-9 w-9 p-0"
                          title="Delete user"
                          data-testid={`delete-user-${u.id}`}
                        >
                          <Trash size={14} weight="bold" />
                        </Button>
                      </div>
                    ) : (
                      <span className="text-[10px] tracking-[0.25em] uppercase text-neutral-400">You</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

const InviteUserDialog = ({ open, onOpenChange, onCreated }) => {
  const [form, setForm] = useState({ email: "", role: "bidder", name: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const reset = () => {
    setForm({ email: "", role: "bidder", name: "" });
    setError("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.post("/invites", form);
      toast.success(`Invite sent to ${form.email}`);
      reset();
      onOpenChange(false);
      onCreated();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogTrigger asChild>
        <Button
          className="rounded-none bg-[#22D3EE] hover:bg-[#0891B2] text-[#0B1929] font-semibold h-10 px-4 btn-sharp"
          data-testid="invite-user-btn"
        >
          <UserPlus size={14} weight="bold" className="mr-2" />
          Invite user
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-none" data-testid="invite-user-dialog">
        <DialogHeader>
          <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-1">User Invitation</div>
          <DialogTitle className="font-display font-extrabold text-2xl tracking-tightest">Invite a user</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="invite-user-form">
          <div className="space-y-2">
            <Label className="text-[10px] tracking-[0.25em] uppercase font-semibold">Email</Label>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
              className="rounded-none border-black/20 h-11"
              placeholder="name@company.com"
              data-testid="invite-email-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-[10px] tracking-[0.25em] uppercase font-semibold">Role</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger className="rounded-none border-black/20 h-11" data-testid="invite-role-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="bidder" data-testid="role-option-bidder">Bidder — can submit quotes on auctions they&apos;re invited to</SelectItem>
                <SelectItem value="admin" data-testid="role-option-admin">Admin — can create auctions and manage users</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {error && (
            <div className="text-sm text-red-600 border border-red-200 bg-red-50 p-3" data-testid="invite-error">
              {error}
            </div>
          )}
          <DialogFooter>
            <Button
              type="submit"
              disabled={busy}
              className="rounded-none bg-[#0B1929] text-white h-11 btn-sharp"
              data-testid="invite-submit-btn"
            >
              {busy ? "Sending..." : "Send invite"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
